/*
  ProjectsBulkEditDialog.jsx — Bulk inline editor for projects (name, type,
  amount, status, date, billing lines). Contains the internal
  BulkBillingLinesPopover helper. Extracted from CollectionPlanningPage.jsx.
*/
import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Search, Save, ListChecks, Loader2, CheckCircle, Plus, Trash2,
} from "lucide-react";
import { REVENUE_STREAMS, BILLING_TYPES, BILLING_FREQUENCIES, sanitizeNumeric, fmt, allowedFrequenciesForType, defaultFrequencyForType } from "./planningUtils";

function BulkBillingLinesPopover({ row, onChange }) {
  const [open, setOpen] = useState(false);
  const lines = row.billing_lines || [];
  const totalOneTime = lines.filter(l => (l.frequency || "one_time") === "one_time").reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const recurringCount = lines.filter(l => (l.frequency || "one_time") !== "one_time").length;
  const addLine = () => onChange([...lines, { id: `new-${Date.now()}`, type: "Others", frequency: "one_time", amount: 0, start_date: row.start_date || "", notes: "" }]);
  const updateLine = (idx, patch) => onChange(lines.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const removeLine = (idx) => onChange(lines.filter((_, i) => i !== idx));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-[11px] font-medium justify-start gap-1 w-full" data-testid={`bulk-row-billing-btn-${row.id}`}>
          <ListChecks className="w-3 h-3 text-[#0066CC] shrink-0" />
          <span>{lines.length} line{lines.length !== 1 ? "s" : ""}</span>
          {totalOneTime > 0 && <span className="text-slate-500 ml-1">· one-time {fmt(totalOneTime)}</span>}
          {recurringCount > 0 && <span className="text-slate-500 ml-1">· {recurringCount} recur.</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[680px] p-3" data-testid={`bulk-row-billing-popover-${row.id}`}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold">Billing Lines · {row.name}</p>
          <Button type="button" variant="outline" size="sm" className="h-7 text-[11px] gap-1" onClick={addLine} data-testid={`bulk-billing-add-${row.id}`}>
            <Plus className="w-3 h-3" /> Add
          </Button>
        </div>
        {lines.length === 0 ? (
          <p className="text-[11px] text-slate-400 italic px-2 py-3 text-center">No lines yet.</p>
        ) : (
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
            {lines.map((bl, idx) => (
              <div key={bl.id || idx} className="grid grid-cols-12 gap-1.5 items-center">
                <select className="col-span-3 h-7 text-[11px] border border-slate-200 rounded px-1 bg-white" value={bl.type} onChange={e => { const newType = e.target.value; const nextFreq = defaultFrequencyForType(newType, bl.frequency); updateLine(idx, { type: newType, frequency: nextFreq }); }} data-testid={`bulk-billing-type-${row.id}-${idx}`}>
                  {BILLING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select className="col-span-2 h-7 text-[11px] border border-slate-200 rounded px-1 bg-white" value={bl.frequency} onChange={e => updateLine(idx, { frequency: e.target.value })} data-testid={`bulk-billing-freq-${row.id}-${idx}`}>
                  {allowedFrequenciesForType(bl.type).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
                <Input type="text" inputMode="numeric" className="col-span-2 h-7 text-[11px] font-mono"
                  value={bl.amount ? String(bl.amount) : ""}
                  onFocus={(e) => e.target.select()}
                  onChange={e => { const c = sanitizeNumeric(e.target.value); updateLine(idx, { amount: c === "" ? 0 : parseFloat(c) }); }}
                  placeholder="Amount" data-testid={`bulk-billing-amount-${row.id}-${idx}`} />
                <Input type="date" className="col-span-2 h-7 text-[11px]" value={bl.start_date || ""} onChange={e => updateLine(idx, { start_date: e.target.value })} data-testid={`bulk-billing-start-${row.id}-${idx}`} title="Start date" />
                <Input type="date" className="col-span-2 h-7 text-[11px]" value={bl.end_date || ""} onChange={e => updateLine(idx, { end_date: e.target.value })} data-testid={`bulk-billing-end-${row.id}-${idx}`} title="End date (optional — no billing after this date)" placeholder="End date" />
                <Button type="button" variant="ghost" size="sm" className="col-span-1 h-7 w-7 p-0 text-red-500 justify-self-end" onClick={() => removeLine(idx)} data-testid={`bulk-billing-remove-${row.id}-${idx}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-slate-400 mt-2">Changes here mark the row as unsaved. Click the row&apos;s Save icon (or Save All) to commit.</p>
      </PopoverContent>
    </Popover>
  );
}

export default function ProjectsBulkEditDialog({ open, onOpenChange, projects, onSaved }) {
  const [rows, setRows] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setRows(projects.map(p => ({
      id: p.id,
      name: p.name || "",
      project_type: p.project_type || "",
      project_amount: p.project_amount || 0,
      status: p.status || "active",
      start_date: p.start_date || "",
      _dirty: false,
    })));
  }, [open, projects]);

  const update = (id, patch) => setRows(rs => rs.map(r => r.id === id ? { ...r, ...patch, _dirty: true } : r));

  const saveRow = async (row) => {
    setSavingId(row.id);
    try {
      await api.put(`/collections/projects/${row.id}`, {
        name: row.name,
        project_type: row.project_type,
        project_amount: Number(row.project_amount) || 0,
        status: row.status,
        start_date: row.start_date,
        billing_lines: row.billing_lines || [],
      });
      setRows(rs => rs.map(r => r.id === row.id ? { ...r, _dirty: false } : r));
      toast.success(`Saved: ${row.name}`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSavingId(null);
  };

  const filtered = rows.filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()));
  const dirtyCount = rows.filter(r => r._dirty).length;
  const saveAllDirty = async () => { for (const r of rows.filter(x => x._dirty)) await saveRow(r); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1200px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-edit-projects-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5 text-[#0066CC]" /> Bulk Edit Projects</DialogTitle>
          <DialogDescription>Inline-edit project name, type, amount, status and start date. Save each row individually or use Save All.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2 py-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input className="pl-8 h-9" placeholder="Filter projects..." value={search} onChange={e => setSearch(e.target.value)} data-testid="bulk-edit-search" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{dirtyCount} unsaved</span>
            <Button onClick={saveAllDirty} disabled={!dirtyCount || !!savingId} className="bg-[#0066CC] hover:bg-[#0052A3] gap-1.5 h-9" data-testid="bulk-edit-save-all">
              <Save className="w-3.5 h-3.5" /> Save All ({dirtyCount})
            </Button>
          </div>
        </div>
        <div className="overflow-auto flex-1 border border-slate-200 rounded-lg">
          <table className="w-full text-xs" data-testid="bulk-edit-table">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[260px]">Project Name</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[180px]">Type</th>
                <th className="text-right p-2 font-semibold text-slate-600 min-w-[140px]">Amount (₹)</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[110px]">Status</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[140px]">Start Date</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[200px]">Billing Lines</th>
                <th className="text-center p-2 font-semibold text-slate-600 sticky right-0 bg-slate-50 w-[80px]">Save</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className={`border-t border-slate-100 ${r._dirty ? "bg-amber-50/40" : "hover:bg-slate-50"}`}>
                  <td className="p-1.5"><Input className="h-8 text-xs" value={r.name} onChange={e => update(r.id, { name: e.target.value })} data-testid={`bulk-row-name-${r.id}`} /></td>
                  <td className="p-1.5">
                    <select value={r.project_type} onChange={e => update(r.id, { project_type: e.target.value })} className="w-full h-8 text-xs border border-slate-200 rounded px-2" data-testid={`bulk-row-type-${r.id}`}>
                      {REVENUE_STREAMS.filter(s => !s.legacy).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      {REVENUE_STREAMS.find(s => s.key === r.project_type)?.legacy && <option value={r.project_type}>{REVENUE_STREAMS.find(s => s.key === r.project_type).label}</option>}
                    </select>
                  </td>
                  <td className="p-1.5"><Input type="number" className="h-8 text-xs text-right tabular-nums" value={r.project_amount} onChange={e => update(r.id, { project_amount: e.target.value })} data-testid={`bulk-row-amount-${r.id}`} /></td>
                  <td className="p-1.5">
                    <select value={r.status} onChange={e => update(r.id, { status: e.target.value })} className="w-full h-8 text-xs border border-slate-200 rounded px-2" data-testid={`bulk-row-status-${r.id}`}>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="on_hold">On Hold</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td className="p-1.5"><Input type="date" className="h-8 text-xs" value={r.start_date} onChange={e => update(r.id, { start_date: e.target.value })} data-testid={`bulk-row-date-${r.id}`} /></td>
                  <td className="p-1.5">
                    <BulkBillingLinesPopover row={r} onChange={(lines) => update(r.id, { billing_lines: lines })} />
                  </td>
                  <td className="p-1.5 sticky right-0 bg-white text-center">
                    <Button size="sm" variant="ghost" disabled={!r._dirty || savingId === r.id} onClick={() => saveRow(r)} className="h-8 w-full text-xs" data-testid={`bulk-row-save-${r.id}`}>
                      {savingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (r._dirty ? <Save className="w-3.5 h-3.5 text-[#0066CC]" /> : <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />)}
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-slate-400 text-xs">No projects match filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 italic">Tip: rows turn yellow when modified. Click the row&apos;s Save icon, or use Save All to commit every change at once.</p>
      </DialogContent>
    </Dialog>
  );
}
